/* eslint-disable react/require-default-props */
import React, {useEffect, useRef} from 'react';
import {
  AccessibilityInfo,
  findNodeHandle,
  InteractionManager,
  View,
} from 'react-native';

import Avatar from '/component/base/Avatar';
import KeyboardFocusable from '/component/base/ExtKeyboard/KeyboardFocusable';
import Text from '/component/base/Text';
import {ManageMySiteTestIDs} from '/constant/TestIDs';
import {tt} from '/provider/TranslationProvider/TranslationProvider.util';

import {formatImageHint, IMAGE_SPECS} from '../../ManageMySite.constants';
import {SectionTitle} from '../../widgets/SectionTitle';
import {StaticFocusable} from '../StaticFocusable';

import {styles} from './AvatarSection.style';
import type {AvatarSectionProps, PickedImage} from './AvatarSection.types';

const {avatarSection} = ManageMySiteTestIDs;

const avatarHint = formatImageHint(
  IMAGE_SPECS.avatar.width,
  IMAGE_SPECS.avatar.height,
);

const ConfirmCancelRow = ({
  onConfirm,
  onCancel,
  isUploading = false,
  confirmButtonRef,
  cancelButtonRef,
}: {
  onConfirm: () => void;
  onCancel: () => void;
  isUploading?: boolean;
  confirmButtonRef?: React.RefObject<any>;
  cancelButtonRef?: React.RefObject<any>;
}) => (
  <View style={styles.actionRow} testID={avatarSection.confirmRow}>
    <KeyboardFocusable
      testID={avatarSection.confirmButton}
      accessibilityRole="button"
      accessibilityLabel={isUploading ? 'Uploading...' : 'Confirm'}
      accessibilityHint={
        isUploading
          ? 'Please wait while the profile photo is uploading'
          : 'Confirms the selected profile photo'
      }
      accessibilityState={{disabled: isUploading}}
      disabled={isUploading}
      onPress={onConfirm}
      viewRef={confirmButtonRef}
      style={styles.confirmBtn}>
      <Text
        style={styles.confirmBtnText}
        accessible={false}
        allowFontScaling
        maxFontSizeMultiplier={2}>
        {isUploading
          ? tt('uploading', 'Uploading...')
          : tt('confirm', 'Confirm')}
      </Text>
    </KeyboardFocusable>

    {!isUploading && (
      <KeyboardFocusable
        testID={avatarSection.cancelButton}
        accessibilityRole="button"
        accessibilityLabel="Cancel"
        accessibilityHint="Cancels the selected profile photo"
        onPress={onCancel}
        viewRef={cancelButtonRef}
        style={styles.cancelBtn}>
        <Text
          style={styles.cancelBtnText}
          accessible={false}
          allowFontScaling
          maxFontSizeMultiplier={2}>
          {tt('cancel', 'Cancel')}
        </Text>
      </KeyboardFocusable>
    )}
  </View>
);

const ChangePhotoButton = ({
  onPress,
  disabled = false,
  changePhotoButtonRef,
}: {
  onPress: (imageData?: PickedImage) => void;
  disabled?: boolean;
  changePhotoButtonRef?: React.RefObject<any>;
}) => (
  <KeyboardFocusable
    testID={avatarSection.changePhotoButton}
    onPress={() => onPress()}
    disabled={disabled}
    accessible
    accessibilityRole="button"
    accessibilityLabel="Change Photo"
    accessibilityHint="Opens image picker to change profile photo"
    accessibilityState={{disabled}}
    viewRef={changePhotoButtonRef}>
    <Text
      allowFontScaling
      maxFontSizeMultiplier={2}
      style={styles.btnChangePhotoText}
      testID={avatarSection.changePhotoText}
      accessible={false}
      isBold>
      {tt('changePhoto', 'Change Photo')}
    </Text>
  </KeyboardFocusable>
);

const RetryUpload = ({
  onRetry,
  onCancel,
  retryButtonRef,
  cancelButtonRef,
}: {
  onRetry: () => void;
  onCancel: () => void;
  retryButtonRef?: React.RefObject<any>;
  cancelButtonRef?: React.RefObject<any>;
}) => (
  <View testID={avatarSection.errorContainer} style={styles.retryContainer}>
    <StaticFocusable
      label="Upload failed. Try again."
      role="alert"
      style={styles.retryTextContainer}>
      <Text style={styles.errorText} accessible={false}>
        {tt('uploadError', 'Upload failed. Try again.')}
      </Text>
    </StaticFocusable>

    <View style={styles.retryActionRow}>
      <KeyboardFocusable
        testID={avatarSection.retryButton}
        onPress={onRetry}
        accessible
        accessibilityRole="button"
        accessibilityLabel="Retry Upload"
        accessibilityHint="Attempts to upload the selected profile photo again"
        viewRef={retryButtonRef}
        style={styles.retryBtn}>
        <Text
          style={styles.retryBtnText}
          accessible={false}
          allowFontScaling
          maxFontSizeMultiplier={2}>
          {tt('retry', 'Retry Upload')}
        </Text>
      </KeyboardFocusable>

      <KeyboardFocusable
        testID={avatarSection.cancelButton}
        onPress={onCancel}
        accessible
        accessibilityRole="button"
        accessibilityLabel="Cancel"
        accessibilityHint="Cancels the failed upload and returns to change photo option"
        viewRef={cancelButtonRef}
        style={styles.retryCancelButton}>
        <Text
          style={styles.retryCancelText}
          accessible={false}
          allowFontScaling
          maxFontSizeMultiplier={2}>
          {tt('cancel', 'Cancel')}
        </Text>
      </KeyboardFocusable>
    </View>
  </View>
);

export const AvatarSection = ({
  avatarUrl = '',
  previewUrl = '',
  onChangePhoto,
  onConfirmPhoto,
  onCancelPhoto,
  fullName = '',
  isUploading = false,
  avatarStatus = 'empty',
  onRetryPhoto,
}: AvatarSectionProps) => {
  const changePhotoButtonRef = useRef<any>(null);
  const confirmButtonRef = useRef<any>(null);
  const cancelButtonRef = useRef<any>(null);
  const retryButtonRef = useRef<any>(null);

  const prevAvatarStatus = useRef(avatarStatus);

  useEffect(() => {
    const prevStatus = prevAvatarStatus.current;
    prevAvatarStatus.current = avatarStatus;

    const focusElement = (ref: React.RefObject<any>, delay = 300) => {
      if (!ref.current) {
        return undefined;
      }

      const timeoutId = setTimeout(() => {
        InteractionManager.runAfterInteractions(() => {
          const node = findNodeHandle(ref.current);

          if (node) {
            AccessibilityInfo.setAccessibilityFocus(node);
          }
        });
      }, delay);

      return () => clearTimeout(timeoutId);
    };

    if (prevStatus === 'empty' && avatarStatus === 'preview') {
      return focusElement(confirmButtonRef);
    }

    if (prevStatus === 'uploading' && avatarStatus === 'uploaded') {
      return focusElement(changePhotoButtonRef);
    }

    if (
      (prevStatus === 'preview' || prevStatus === 'error') &&
      avatarStatus === 'empty'
    ) {
      return focusElement(changePhotoButtonRef);
    }

    if (prevStatus === 'uploaded' && avatarStatus === 'preview') {
      return focusElement(confirmButtonRef);
    }

    if (prevStatus === 'preview' && avatarStatus === 'uploaded') {
      return focusElement(changePhotoButtonRef, 100);
    }

    return undefined;
  }, [avatarStatus]);

  useEffect(() => {
    if (avatarStatus !== 'error') {
      return undefined;
    }

    const timeoutId = setTimeout(() => {
      InteractionManager.runAfterInteractions(() => {
        const node = findNodeHandle(retryButtonRef.current);

        if (node) {
          AccessibilityInfo.setAccessibilityFocus(node);
        }
      });
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [avatarStatus]);

  const displayUrl =
    avatarStatus === 'preview' ||
    avatarStatus === 'uploading' ||
    avatarStatus === 'error'
      ? previewUrl
      : avatarUrl;

  const avatarAccessibilityLabel = displayUrl
    ? fullName
      ? `${fullName}, profile picture`
      : 'Profile picture'
    : 'No profile picture selected';

  return (
    <View style={styles.section} testID={avatarSection.container}>
      <SectionTitle title={tt('contact', 'Coaching Profile Image')} />

      <View style={styles.avatarRow} accessible={false}>
        <View
          testID={avatarSection.image}
          accessible
          focusable
          accessibilityRole="image"
          accessibilityLabel={avatarAccessibilityLabel}>
          <Avatar
            key={`${avatarStatus}-${displayUrl || 'empty'}`}
            nativeID={avatarSection.image}
            avatarUrl={displayUrl || null}
            size={styles.avatarCircle.width}
            accessible={false}
            importantForAccessibility="no-hide-descendants"
            fullName={fullName}
          />
        </View>

        <View style={styles.avatarMeta}>
          {avatarStatus === 'error' ? (
            <RetryUpload
              onRetry={onRetryPhoto || onConfirmPhoto}
              onCancel={onCancelPhoto}
              retryButtonRef={retryButtonRef}
              cancelButtonRef={cancelButtonRef}
            />
          ) : avatarStatus === 'preview' || avatarStatus === 'uploading' ? (
            <ConfirmCancelRow
              onConfirm={onConfirmPhoto}
              onCancel={onCancelPhoto}
              isUploading={isUploading}
              confirmButtonRef={confirmButtonRef}
              cancelButtonRef={cancelButtonRef}
            />
          ) : (
            <ChangePhotoButton
              onPress={onChangePhoto}
              disabled={isUploading}
              changePhotoButtonRef={changePhotoButtonRef}
            />
          )}
        </View>
      </View>

      <StaticFocusable testID={avatarSection.hint} label={avatarHint}>
        <Text
          allowFontScaling
          maxFontSizeMultiplier={2}
          style={styles.avatarHint}
          testID={avatarSection.hintText}
          accessible={false}>
          {tt('avatarHint', avatarHint)}
        </Text>
      </StaticFocusable>
    </View>
  );
};

export default AvatarSection;
